import { API_PATHS } from '@/constants/api-paths'
import { api, type AppRequestConfig } from '@/lib/api'
import { getMapSearchCity } from '@/lib/map-location'
import { isInAppChildServiceVariant, isOrganizationServiceVariant } from '@/lib/service-variant-types'

type UnknownRecord = Record<string, unknown>

const JOB_MANAGEMENT_VARIANT_ID = '1500'
const JOB_MANAGEMENT_VARIANT_LABEL = 'job management'
const getListPayload = <T>(payload: unknown): T[] => {
  if (Array.isArray(payload)) {
    return payload as T[]
  }

  if (payload && typeof payload === 'object') {
    const record = payload as UnknownRecord
    if (Array.isArray(record.data)) {
      return record.data as T[]
    }
    if (Array.isArray(record.results)) {
      return record.results as T[]
    }
    if (record.data && typeof record.data === 'object' && Array.isArray((record.data as UnknownRecord).results)) {
      return (record.data as UnknownRecord).results as T[]
    }
  }

  return []
}

const getObjectPayload = <T>(payload: unknown): T | null => {
  if (!payload || typeof payload !== 'object') {
    return null
  }

  const record = payload as UnknownRecord
  if (record.data && typeof record.data === 'object' && !Array.isArray(record.data)) {
    return record.data as T
  }

  return record as T
}

/** Full Get payload uses `instance-id`; list uses `instance_id`. Normalize so the rest of the app can use `instance_id`. */
function normalizeServiceInstanceApiRow<T extends OrgTreeServiceInstance>(row: T | null | undefined): T | null {
  if (row == null) {
    return null
  }

  const raw = row as unknown as Record<string, unknown>
  const hyphen = raw['instance-id']
  const fromHyphen = String(hyphen ?? '').trim()
  const serviceIdFallback = String(raw.service_id ?? '').trim()
  const serviceIdLooksLikeInstanceId = /[A-Za-z_-]/.test(serviceIdFallback)
  const merged = String(row.instance_id ?? '').trim()
    || fromHyphen
    || (fromHyphen || !serviceIdLooksLikeInstanceId ? '' : serviceIdFallback)

  const nodeConfig = (
    raw.node_config_data
    && typeof raw.node_config_data === 'object'
    && !Array.isArray(raw.node_config_data)
  )
    ? raw.node_config_data as Record<string, unknown>
    : null

  const privacyFromConfig = nodeConfig?.privacy_policy
  const termsFromConfig = nodeConfig?.terms_and_conditions
  const topPrivacy = String(row.privacy_policy ?? '').trim()
  const topTerms = String(row.terms_and_conditions ?? '').trim()

  const liftedPrivacy = topPrivacy
    || (typeof privacyFromConfig === 'string' ? privacyFromConfig.trim() : '')
    || null
  const liftedTerms = topTerms
    || (typeof termsFromConfig === 'string' ? termsFromConfig.trim() : '')
    || null

  const nextRow = {
    ...row,
    ...(merged ? { instance_id: merged } : {}),
    ...(liftedPrivacy && !topPrivacy ? { privacy_policy: liftedPrivacy } : {}),
    ...(liftedTerms && !topTerms ? { terms_and_conditions: liftedTerms } : {}),
  } as T

  return nextRow
}

const getCatalogListPayload = <T>(payload: unknown): T[] => {
  if (!payload || typeof payload !== 'object') {
    return []
  }

  const record = payload as UnknownRecord
  if (record.data && typeof record.data === 'object' && Array.isArray((record.data as UnknownRecord).list)) {
    return (record.data as UnknownRecord).list as T[]
  }

  if (Array.isArray(record.list)) {
    return record.list as T[]
  }

  return []
}

const unwrapMutationPayload = <T>(payload: unknown): T => {
  if (payload && typeof payload === 'object') {
    const record = payload as UnknownRecord
    const status = String(record.status ?? '').trim().toLowerCase()
    if (status === 'failed' || status === 'error' || status === 'false') {
      const message = String(record.message ?? '').trim() || 'Request failed'
      throw new Error(message)
    }

    if ('data' in record) {
      return (record.data as T) ?? (record as T)
    }
  }

  return payload as T
}

const isBusinessServiceVariant = (row: UnknownRecord) => {
  const id = String(row.id ?? '')
  const label = String(row.value ?? row.name ?? row.type ?? '').trim().toLowerCase()

  return id !== JOB_MANAGEMENT_VARIANT_ID && label !== JOB_MANAGEMENT_VARIANT_LABEL
}

const getServiceVariantLabel = (row: UnknownRecord) =>
  String(row.value ?? row.name ?? row.type ?? row.id ?? 'Variant')

const getServiceVariantUniqueId = (row: UnknownRecord) =>
  String(row.service_variant_unique_id ?? row.unique_service_id ?? row.service_variant ?? row.id ?? '').trim()

const getServiceUniqueId = (row: UnknownRecord) =>
  (row.service_unique_id as string | number | null | undefined)
  ?? (row.service_variant_unique_id as string | number | null | undefined)
  ?? null

const STRUCTURAL_BRANCH_VARIANT_LABELS = new Set(['branch'])

/** Only real business service variants; exclude org-scope and the structural BRANCH assignment row. */
const isBranchServiceVariant = (row: UnknownRecord) =>
  !isOrganizationServiceVariant(getServiceVariantUniqueId(row), getServiceVariantLabel(row))
  && !STRUCTURAL_BRANCH_VARIANT_LABELS.has(getServiceVariantLabel(row).trim().toLowerCase())

export interface OrgTreeNode {
  id: string | number
  node_name?: string | null
  parent?: string | number | null
  service_variant?: string | null
  service_variant_name?: string | null
  level_variant_name?: string | null
  service_instance_data?: Array<{
    instance_id?: string | null
    id?: string | number | null
    name?: string | null
    instance_name?: string | null
    service_id?: string | number | null
    service_name?: string | null
  }> | null
}

export interface OrgTreeNodeConfig {
  privacy_policy?: string | null
  terms_and_conditions?: string | null
  about_us?: string | null
  copyright?: string | null
  return_details?: string | null
  refund_details?: string | null
}

export interface OrgTreeNodeAddress {
  id?: string | number
  node?: string | number
  name?: string | null
  address_for?: string | null
  fname?: string | null
  lname?: string | null
  fline?: string | null
  sline?: string | null
  door_no?: string | null
  zipcode?: string | null
  country?: string | number | null
  country_name?: string | null
  county?: string | number | null
  county_name?: string | null
  state?: string | number | null
  state_name?: string | null
  city?: string | number | null
  city_name?: string | null
  latitude?: string | number | null
  longitude?: string | number | null
     active?: number | null
}

export interface OrgTreeNodeSettingItem {
  id: string | number
  key?: string | null
  text?: string | null
  item_key?: string | null
  item_text?: string | null
  data_type?: string | number | null
  value?: string | string[] | null
  icon?: string | null
}

export interface OrgTreeServiceArea {
  id: string | number
  node?: string | number | null
  area?: string | null
  postcode?: string | null
  city_name?: string | null
  country?: string | null
  county?: string | null
  state?: string | null
  district?: string | null
}

export interface OrgTreeCatalogItem {
  key: string
  text: string
}

export interface OrgTreeSettingCatalogOption {
  key: string
  text: string
  data_type?: string | number | null
  items: OrgTreeCatalogItem[]
}

export interface OrgTreeAddressPayload {
  node?: string | number | null
  name?: string | null
  address_for?: string | null
  fname?: string | null
  lname?: string | null
  door_no?: string | null
  fline?: string | null
  sline?: string | null
  zipcode?: string | null
  country?: string | number | null
  state?: string | number | null
  city_name?: string | null
  latitude?: string | number | null
  longitude?: string | number | null
}

export interface OrgTreeNodeSettingPayload {
  node: string | number
  key: string
  text: string
  item_key: string
  item_text: string
  data_type: string | number
  value: string
}

export interface OrgTreeNodeSettingUpdatePayload {
  node?: string | number
  value: string
}

export interface OrgTreeServiceAreaPayload {
  node: string | number
  area?: string | null
  postcode?: string | null
  city_name?: string | null
  district?: string | null
  country?: string | number | null
  state?: string | number | null
}

export interface OrgTreeBusinessHour {
  id: string
  _id?: string | number | null
  business_day?: string | null
  _business_day?: string | number | null
  label?: string | null
  start_time?: string | null
  end_time?: string | null
}

export interface OrgTreeBusinessDay {
  id: string
  _id?: string | number | null
  label?: string | null
  day?: number | null
  type?: number | null
  date_from?: string | null
  date_to?: string | null
  message?: string | null
  is_holiday?: boolean | null
  business_hour_data?: OrgTreeBusinessHour[] | null
}

export interface OrgTreeBusinessDayPayload {
  label?: string | null
  day?: number | null
  type: number
  message?: string | null
  is_holiday: boolean
}

export interface OrgTreeBusinessHourPayload {
  business_day: string
  label?: string | null
  start_time: string
  end_time: string
}

export interface OrgTreeInstanceVariant {
  id: string | number
  name: string
  service_variant?: string | null
  serviceId?: string | number | null
}

export interface OrgTreeServiceVariant {
  id: string | number
  name: string
  value?: string | null
  serviceId?: string | number | null
  serviceName?: string | null
  serviceUniqueId?: string | number | null
}

export interface OrgTreeUserProfile {
  user_id?: string | number | null
  name?: string | null
  email?: string | null
  phone?: string | null
  username?: string | null
}

export interface OrgTreeServiceInstance {
  id?: string | number
  config?: Record<string, unknown> | null
  mobile_app_enabled?: boolean | number | string | null
  privacy_policy?: string | null
  terms_and_conditions?: string | null
  node_config_data?: {
    privacy_policy?: string | null
    terms_and_conditions?: string | null
    about_us?: string | null
    copyright?: string | null
    [key: string]: unknown
  } | null
  instance_id?: string | null
  service_id?: string | null
  /** Backend list/detail: scoped org identifier (tenant/branch scope). */
  scoped_local_id?: string | number | null
  tenant_id?: string | number | null
  branch_id?: string | number | null
  name?: string | null
  title?: string | null
  type?: string | null
  status?: string | number | null
  created_on?: string | null
  created_by?: string | number | null
  created_by_name?: string | null
  updated_on?: string | null
  updated_by?: string | number | null
  updated_by_name?: string | null
  identifier?: string | null
  url_identifier?: string | null
  parent_instance_id?: string | null
  parent_instance_name?: string | null
  parent_instance_identifier?: string | null
  account_nbr?: string | null
  account_nbr_id?: string | number | null
  admin_domain?: string | null
  ui_domain?: string | null
  email?: string | null
  phn_nbr?: string | null
  phone?: string | null
  location?: string | null
  latitude?: string | number | null
  longitude?: string | number | null
  lat?: string | number | null
  lng?: string | number | null
  lon?: string | number | null
  address_line_1?: string | null
  address_line_2?: string | null
  city?: string | null
  postcode?: string | null
  zipcode?: string | null
  service_variant?: string | number | null
  service_variant_label?: string | null
  service_variant_name?: string | null
  service_variant_type?: string | null
  /** List serializer: `service_variant__service__services_name`. */
  service_variant_service?: string | null
  /** Hierarchy tier — backend: LevelVariants id. */
  level_variant?: number | null
  level_variant_unique_id?: string | null
  level_variant_name?: string | null
  tier_code?: string | null
  vertical_id?: number | null
  /** Vertical.system_code when catalog tier (ROOT_ORG, TENANT, BRANCH). */
  level_variant_vertical_system_code?: string | null
  vertical_name?: string | null
  service_data?: {
    service_id?: string | null
    service_name?: string | null
    variant_unique?: string | number | null
    service_type?: string | null
  } | null
  primary_node?: string | number | null
  currency_id?: string | number | null
  currency_code?: string | null
  currency_symbol?: string | null
  time_zone_id?: string | number | null
  time_zone?: string | number | null
  country_id?: string | number | null
  country_name?: string | null
  country_distance_unit?: 'metric' | 'imperial' | string | null
  /** ISO 3166-1 alpha-2 when provided by API (generic list may omit). */
  country_code?: string | null
  state?: string | number | null
  state_id?: string | number | null
  user_name?: string | null
  password?: string | null
  base_url?: string | null
  static_file_path?: string | null
  default_local_storage_path?: string | null
  regex?: string | null
  regex_message?: string | null
  verification_email_url?: string | null
  certificate_path?: string | null
  certificate_coordinates?: string | null
  donate_domain?: string | null
  primary_color?: string | null
  copyright?: string | null
  sms_gateway_type?: string | null
  twilio_account_sid?: string | null
  twilio_auth_token?: string | null
  twilio_phone_number?: string | null
  twilio_service_id?: string | null
  fcm_url?: string | null
  fcm_server_key?: string | null
  fcm_project_id?: string | null
  google_application_credentials_json?: string | null
  google_application_credentials_path?: string | null
  google_map_api_key?: string | null
  map_search_provider?: string | null
  webxpay_login_api_url?: string | null
  webxpay_retrieval_api_url?: string | null
  order_email_plain_text?: boolean | null
  storage_type?: string | null
  resolved_storage_type?: string | null
  resolved_static_file_path?: string | null
  storage_access_key?: string | null
  storage_secret_key?: string | null
  storage_bucket_name?: string | null
  storage_region_name?: string | null
  email_host?: string | null
  email_port?: string | null
  email_host_user?: string | null
  email_host_password?: string | null
  mobile_otp_api_key?: string | null
  mobile_otp_api_url?: string | null
  mobile_otp_sender?: string | null
  company_registration_body?: string | null
  company_registration_name?: string | null
  company_registration_number?: string | null
  company_tax_body?: string | null
  company_tax_name?: string | null
  company_tax_number?: string | null
  tax_included_in_prices?: boolean | null
  delivery_system?: string | number | null
  logo?: string | null
  login_page_logo?: string | null
  side_panel_logo?: string | null
  welcome_page_logo?: string | null
  header_text_logo?: string | null
  service_instance_logo?: string | null
  home_page_logo?: string | null
  home_banner_logo?: string | null
  company_logo?: string | null
  app_logo?: string | null
  service_logo?: string | null
  image?: string | null
  icon?: string | null
  org_logo?: string | null
  organization_logo?: string | null
  organisation_logo?: string | null
  banner?: string | null
  contribution_certificate?: string | null
  donate_certificate?: string | null
  expense_certificate?: string | null
  org_id_card?: string | null
  owner_user_id?: string | number | null
  owner_name?: string | null
  owner_email?: string | null
  owner_phone?: string | null
  owner_profile?: OrgTreeUserProfile | null
  branch_admin_user_id?: string | number | null
  branch_admin_name?: string | null
  branch_admin_email?: string | null
  branch_admin_phone?: string | null
  branch_admin_profile?: OrgTreeUserProfile | null
  branch_admin_profiles?: OrgTreeUserProfile[] | null
  branch_admin_count?: number | null
  /** List serializer alias for primary branch admin (same as branch_admin_*). */
  admin_user_id?: string | number | null
  admin_name?: string | null
  admin_email?: string | null
  admin_phone?: string | null
  primary_role_id?: string | number | null
  primary_role_title?: string | null
  primary_role_unique_id?: string | null
  primary_role_user_id?: string | number | null
  primary_role_name?: string | null
  primary_role_email?: string | null
  primary_role_phone?: string | null
  primary_role_profiles?: OrgTreeUserProfile[] | null
  primary_role_count?: number | null
  support_user_id?: string | number | null
  support_name?: string | null
  support_email?: string | null
  support_phone?: string | null
  login_enabled?: boolean | null
  login_with_email?: boolean | null
  login_with_guest?: boolean | null
  login_with_mobile?: boolean | null
  login_with_regnum?: boolean | null
  login_otp_authentication?: boolean | null
  signup_private_active?: boolean | null
  signup_private_fname?: boolean | null
  signup_private_lname?: boolean | null
  signup_private_mobile?: boolean | null
  signup_private_email?: boolean | null
  signup_private_country?: boolean | null
  signup_private_state?: boolean | null
  signup_private_url?: string | null
  signup_public_active?: boolean | null
  signup_public_fname?: boolean | null
  signup_public_lname?: boolean | null
  signup_public_mobile?: boolean | null
  signup_public_email?: boolean | null
  signup_public_country?: boolean | null
  signup_default_user_nativity?: string | null
  signup_public_state?: boolean | null
  signup_public_dob?: boolean | null
  signup_public_age?: boolean | null
  signup_public_gender?: boolean | null
  pwdreset_active?: boolean | null
  pwdreset_linkExpiry?: number | null
  pwdreset_third_party?: boolean | null
  /** Model field is boolean (legacy name “redirect page”). */
  pwdreset_redirect_page?: boolean | null
  pwdreset_type?: number | null
  email_verification_active?: boolean | null
  email_verification_linkExpiry?: number | null
  email_verification_third_party?: boolean | null
  email_verification_redirect_page?: string | null
  email_verification_type?: number | null
  email_verification_mandatory?: boolean | null
  email_verification_length?: number | null
  mobile_verification_active?: boolean | null
  mobile_verification_linkExpiry?: number | null
  mobile_verification_third_party?: boolean | null
  mobile_verification_redirect_page?: string | null
  mobile_verification_type?: number | null
  mobile_verification_mandatory?: boolean | null
  mobile_verification_length?: number | null
  chat_enabled?: boolean | null
  chat_internal_enabled?: boolean | null
  user_visibility_scope?: string | null
  public_user_count?: number | null
  private_user_count?: number | null
  credential_settings?: OrgTreeCredentialSettings | null
}

export const resolveLoginTenantReference = (
  instance: Pick<OrgTreeServiceInstance, 'parent_instance_identifier' | 'parent_instance_id' | 'identifier' | 'instance_id'> | null | undefined,
  fallbackReference?: string | null,
): string => {
  const parentIdentifier = String(instance?.parent_instance_identifier ?? '').trim()
  if (parentIdentifier) {
    return parentIdentifier
  }

  const parentInstanceId = String(instance?.parent_instance_id ?? '').trim()
  if (parentInstanceId) {
    return parentInstanceId
  }

  const identifier = String(instance?.identifier ?? '').trim()
  if (identifier) {
    return identifier
  }

  const instanceId = String(instance?.instance_id ?? '').trim()
  if (instanceId) {
    return instanceId
  }

  return String(fallbackReference ?? '').trim()
}

export interface OrgTreeServiceInstanceMediaUpload {
  id: string | number
  service_variant_key?: string | number | null
  service_instance_id?: string | number | null
  media_key: string
  uploaded_filepath: string
  file_url?: string | null
  created_on?: string | null
  updated_on?: string | null
}

export interface OrgTreeServiceInstanceMediaCard {
  media_key: string
  label: string
  slot_label?: string | null
  permitted_count: number
  /** Same as permitted_count; included for slot config clarity */
  max_files_allowed?: number
  remaining_count?: number
  uploads: OrgTreeServiceInstanceMediaUpload[]
  purpose?: string | null
  display_format?: string | null
  max_file_size_mb?: number | null
  supported_formats?: string[] | null
  width_px?: number | null
  height_px?: number | null
  description?: string | null
}

export interface OrgTreeServiceInstanceMediaState {
  service_instance_id: string | number
  service_instance_db_id?: string | number | null
  service_variant_key?: string | number | null
  service_variant_name?: string | null
  cards: OrgTreeServiceInstanceMediaCard[]
}

export interface OrgTreeCredentialValue {
  configured: boolean
  source: string
  value?: string | boolean | null
  masked_value?: string | null
}

export interface OrgTreeCredentialSection {
  label: string
  configured: boolean
  source: string
  missing_required_fields?: string[]
  [key: string]: OrgTreeCredentialValue | string | boolean | string[] | undefined
}

export interface OrgTreeCommunicationProviderSecretValue {
  configured: boolean
  masked_value?: string | null
  source?: string | null
}

export interface OrgTreeCommunicationProvider {
  key: string
  type: 'email' | 'sms' | 'whatsapp' | 'slack' | string
  label: string
  enabled?: boolean | null
  source?: string | null
  configured?: boolean | null
  missing_required_fields?: string[]
  legacy?: boolean | null
  workspace_name?: string | null
  channel_name?: string | null
  host?: string | number | boolean | null
  port?: string | number | boolean | null
  host_user?: string | null
  plain_text_mode?: string | boolean | null
  gateway_type?: string | null
  sender?: string | null
  api_url?: string | null
  api_version?: string | null
  phone_number_id?: string | null
  host_password?: OrgTreeCommunicationProviderSecretValue
  api_key?: OrgTreeCommunicationProviderSecretValue
  access_token?: OrgTreeCommunicationProviderSecretValue
  bot_token?: OrgTreeCommunicationProviderSecretValue
  webhook_url?: OrgTreeCommunicationProviderSecretValue
}

export interface OrgTreeCommunicationRoute {
  configured: boolean
  source: string
  provider_source?: string | null
  provider_key?: string | null
  route_purpose?: string | null
  legacy?: boolean | null
  missing_required_fields?: string[]
}

export interface OrgTreeCommunicationSettings {
  label: string
  channels: Record<string, string>
  purposes: Record<string, string>
  provider_types?: Record<string, unknown>
  providers: Record<string, OrgTreeCommunicationProvider>
  routes: Record<string, Record<string, OrgTreeCommunicationRoute>>
}

export interface OrgTreeCredentialSettings {
  source: string
  sections: Record<string, OrgTreeCredentialSection>
  communication?: OrgTreeCommunicationSettings | null
}

export interface OrgTreeMapsClientConfig {
  provider?: string | null
  api_key?: string | null
  configured?: boolean | null
  source?: string | null
}

export interface OrgTreeAppSettings {
  service_instance?: string | null
  mobile_app_enabled: boolean
  configured?: boolean | null
  apps: OrgTreeManagedApp[]
  tax_registrations?: OrgTreeTaxRegistration[]
  app_url?: {
    android_url?: string | null
    ios_url?: string | null
  }
}

export interface OrgTreeTaxRegistration {
  country_code: string
  registration_type?: 'TIN' | 'GST' | 'VAT' | 'EIN' | 'OTHER' | string | null
  registration_label?: string | null
  registration_number?: string | null
  issued_by?: string | null
  notes?: string | null
  active?: boolean | null
}

/** One row in release history for a managed app (newest first from API). */
export interface OrgTreeAppVersionHistoryItem {
  version_id: number | null
  version: string
  build_number: string
  force_update_enabled: boolean
  force_update_version?: string | null
  update_message?: string | null
  uploaded_on?: string | null
  is_latest?: boolean
}

export interface OrgTreeManagedApp {
  id: string
  app_key?: string | null
  label: string
  platform: 'android' | 'ios'
  device_type: 'phone' | 'tablet'
  store_url?: string | null
  version?: string | null
  build_number?: string | null
  force_update_enabled: boolean
  force_update_version?: string | null
  update_message?: string | null
  uploaded_on?: string | null
  active: boolean
  /** Release history when stored in AppVersion rows; legacy configs may synthesize a single row. */
  versions?: OrgTreeAppVersionHistoryItem[]
  /** When set, PATCH updates this AppVersion row instead of creating a new release. */
  editing_version_id?: number | null
}

export interface OrgTreeCredentialTestPayload {
  channel: 'email' | 'sms' | 'push' | 'whatsapp' | 'twilio' | 'storage' | 'google_maps' | 'slack'
  destination_email?: string
  destination_phone?: string
  destination_device_id?: string
  destination?: string
  provider_key?: string
  purpose?: string
  subject?: string
  title?: string
  body: string
}

export interface OrgTreeCountry {
  id: string | number
  country_name?: string | null
  country_code?: string | null
  dial_code?: string | null
  phone_number_mask?: string | null
  flag?: string | null
  currency_id?: string | number | null
  currency_name?: string | null
  currency_code?: string | null
  currency_symbol?: string | null
}

export interface OrgTreeState {
  id: string | number
  name: string
}

export interface OrgTreeCurrency {
  id: string | number
  currency_name?: string | null
  currency_code?: string | null
  name?: string | null
}

export interface OrgTreeTimezone {
  id: string | number
  timezone?: string | null
  utc_offset?: string | null
  country_code?: string | null
}

export interface OrgTreeLocationSearchResult {
  id: string
  label: string
  lat: string
  lon: string
  door_no?: string | null
  street?: string | null
  city?: string | null
  zipcode?: string | null
}

export interface OrgTreeLocationSearchResponse {
  provider?: string | null
  configured_provider?: string | null
  provider_source?: string | null
  fallback_used?: boolean | null
  results: OrgTreeLocationSearchResult[]
}

export interface OrgTreeSignupTemplatePreview {
  channel: 'email' | 'sms' | 'push'
  template_key: string
  signup_label: string
  sample_context?: Record<string, unknown>
  preview: {
    subject?: string
    heading?: string
    intro?: string
    title?: string
    message?: string
    html_content?: string
  }
}

export interface OrgTreeSignupTemplatePreviewPayload {
  channel: 'email' | 'sms' | 'push'
  template_key: string
  signup_label?: string
  draft: Record<string, string>
}

type OrgTreeNodeListOptions = {
  childNodes?: boolean
  childNodeType?: number
  serviceInstance?: boolean
}

type ServiceInstancePayload = Record<string, string | number | boolean | null | undefined | Record<string, unknown> | unknown[]>

const getOptionalServiceInstanceRequestConfig = (
  serviceInstanceId?: string | number,
  options?: { lightweight?: boolean },
): AppRequestConfig => ({
  skipAuthRedirectOn401: true,
  // Let backend resolve an accessible post by service_instance_id instead of forcing current post-id.
  skipPostIdHeader: true,
  params: {
    ...(serviceInstanceId != null
      ? {
        service_instance_id: String(serviceInstanceId),
      }
      : {}),
    ...(options?.lightweight === false ? {} : { lightweight: 'true' }),
  },
})

export const orgServiceInstanceLightQueryKey = (serviceInstanceId: string) =>
  ['org-service-instance-light', serviceInstanceId] as const

export const orgTreeApi = {
  createNode: async (payload: {
    node_name: string
    parent_node: string | number
    lvl_variant: string | number
  }) => {
    const response = await api.post(API_PATHS.orgTree.nodes.add, payload)
    return response.data
  },

  getNode: async (nodeId: string | number): Promise<OrgTreeNode | null> => {
    const response = await api.get(API_PATHS.orgTree.nodes.list, {
      params: {
        node_id: nodeId,
        child_nodes: false,
      },
    })

    const directRecord = getObjectPayload<OrgTreeNode>(response.data)
    if (directRecord && (directRecord.id != null || directRecord.node_name != null)) {
      return directRecord
    }

    return getListPayload<OrgTreeNode>(response.data)[0] ?? null
  },

  getNodeWithServiceInstances: async (nodeId: string | number): Promise<OrgTreeNode | null> => {
    const response = await api.get(API_PATHS.orgTree.nodes.list, {
      params: {
        node_id: nodeId,
        child_nodes: false,
        service_instance: true,
      },
    })

    const directRecord = getObjectPayload<OrgTreeNode>(response.data)
    if (directRecord && (directRecord.id != null || directRecord.node_name != null)) {
      return directRecord
    }

    return getListPayload<OrgTreeNode>(response.data)[0] ?? null
  },

  listChildren: async (nodeId: string | number, options: OrgTreeNodeListOptions = {}): Promise<OrgTreeNode[]> => {
    const response = await api.get(API_PATHS.orgTree.nodes.list, {
      params: {
        node_id: nodeId,
        child_nodes: options.childNodes ?? true,
        ...(typeof options.childNodeType === 'number' ? { child_node_type: options.childNodeType } : {}),
        ...(options.serviceInstance ? { service_instance: true } : {}),
        page_size: 1000,
      },
    })

    return getListPayload<OrgTreeNode>(response.data)
  },

  updateNode: async (nodeId: string | number, payload: { node_name: string }) => {
    const response = await api.patch(API_PATHS.orgTree.nodes.update(nodeId), payload)
    return response.data
  },

  deleteNode: async (nodeId: string | number) => {
    const response = await api.delete(API_PATHS.orgTree.nodes.delete(nodeId))
    return response.data
  },

  listInstanceVariants: async (orgInstanceId: string): Promise<OrgTreeInstanceVariant[]> => {
    const response = await api.get(API_PATHS.orgTree.instanceVariants.list, {
      params: {
        org_instance: orgInstanceId,
      },
    })

    const rows = getListPayload<UnknownRecord>(response.data).filter(isBranchServiceVariant)
    return rows.map((row) => ({
      id: (row.id as string | number) ?? '',
      name: String(row.value ?? row.name ?? row.id ?? 'Variant'),
      service_variant: (row.service_variant as string | null | undefined) ?? null,
      serviceId: (row.service_id as string | number | null | undefined) ?? null,
    }))
  },

  updateInstanceVariants: async (orgInstanceId: string, serviceVariantIds: Array<string | number>) => {
    const response = await api.post(API_PATHS.orgTree.instanceVariants.add, {
      org_instance: orgInstanceId,
      service_variant: serviceVariantIds,
    })

    return response.data
  },

  createChildBranch: async (payload: {
    name: string
    service_variant?: string
    org_instance_id: string
    branch_ownership: string
    owner_user_id?: string
    identifier?: string
    url_identifier?: string
    lvl_variant?: string
    parent_service_instance?: string
    owner_type?: string
  }) => {
    const response = await api.post(API_PATHS.orgTree.serviceInstances.add, {
      ...payload,
      lvl_variant: payload.lvl_variant || 'branch',
      parent_service_instance: payload.parent_service_instance || payload.org_instance_id,
      owner_type: payload.owner_type || payload.branch_ownership,
      service_instance: payload.parent_service_instance || payload.org_instance_id,
    })
    return response.data
  },

  createNodeAppInstance: async (payload: {
    branch_service_instance?: string
    branch_node?: string | number
    service_instance?: string | number
    service_variant: string
    name: string
    branch_ownership: string
    owner_user_id?: string
    identifier?: string
    url_identifier?: string
    lvl_variant?: string
    owner_type?: string
  }) => {
    const normalizedPayload: Record<string, unknown> = {
      ...payload,
      owner_type: payload.owner_type || payload.branch_ownership,
    }
    if (normalizedPayload.branch_node) {
      delete normalizedPayload.branch_service_instance
    }

    const response = await api.post(API_PATHS.orgTree.nodeApps.add, normalizedPayload)
    return response.data
  },

  getNodeConfig: async (nodeId: string | number): Promise<OrgTreeNodeConfig | null> => {
    const response = await api.get(API_PATHS.orgTree.config.get(nodeId))
    const payload = getObjectPayload<OrgTreeNodeConfig & {
      privacy_policy_text?: string | null
      terms_and_conditions_text?: string | null
      about_us_text?: string | null
      return_details_text?: string | null
      refund_details_text?: string | null
    }>(response.data)

    if (!payload || typeof payload !== 'object') {
      return null
    }

    // Prefer API text keys; fall back to *_text columns when FK-based serializers omit them.
    return {
      ...payload,
      privacy_policy: payload.privacy_policy ?? payload.privacy_policy_text ?? null,
      terms_and_conditions: payload.terms_and_conditions ?? payload.terms_and_conditions_text ?? null,
      about_us: payload.about_us ?? payload.about_us_text ?? null,
      return_details: payload.return_details ?? payload.return_details_text ?? null,
      refund_details: payload.refund_details ?? payload.refund_details_text ?? null,
    }
  },

  updateNodeConfig: async (nodeId: string | number, payload: Partial<OrgTreeNodeConfig>) => {
    const response = await api.patch(API_PATHS.orgTree.config.update(nodeId), payload)
    return response.data
  },

  listAddresses: async (nodeId: string | number): Promise<OrgTreeNodeAddress[]> => {
    const response = await api.get(API_PATHS.orgTree.address.list, {
      params: {
        node_id: nodeId,
      },
    })

    return getListPayload<OrgTreeNodeAddress>(response.data)
  },

  createAddress: async (nodeId: string | number, payload: OrgTreeAddressPayload) => {
    const response = await api.post(API_PATHS.orgTree.address.create(nodeId), payload)
    return response.data
  },

  updateAddress: async (nodeId: string | number, payload: OrgTreeAddressPayload) => {
    const response = await api.patch(API_PATHS.orgTree.address.update(nodeId), payload)
    return response.data
  },

  deleteAddress: async (nodeId: string | number) => {
    const response = await api.delete(API_PATHS.orgTree.address.delete(nodeId))
    return response.data
  },

     toggleAddressActive: async (nodeId: string | number, active: number) => {
       const response = await api.patch(API_PATHS.orgTree.address.status(nodeId), { active })
       return response.data
     },


  listSocialMedia: async (nodeId: string | number): Promise<OrgTreeNodeSettingItem[]> => {
    const response = await api.get(API_PATHS.orgTree.settings.social, {
      params: {
        node_id: nodeId,
        page: 1,
      },
    })

    const payload = getObjectPayload<{ results?: { social_media_data?: OrgTreeNodeSettingItem[] } }>(response.data)
    return payload?.results?.social_media_data ?? []
  },

  listContacts: async (nodeId: string | number): Promise<OrgTreeNodeSettingItem[]> => {
    const response = await api.get(API_PATHS.orgTree.settings.contacts, {
      params: {
        node_id: nodeId,
        page: 1,
      },
    })

    const payload = getObjectPayload<{ results?: { contact_list_data?: OrgTreeNodeSettingItem[] } }>(response.data)
    return payload?.results?.contact_list_data ?? []
  },

  listSocialMediaTypes: async (): Promise<OrgTreeSettingCatalogOption[]> => {
    const response = await api.get(API_PATHS.orgTree.meta.socialMediaTypes)
    return getCatalogListPayload<OrgTreeSettingCatalogOption>(response.data)
  },

  listContactTypes: async (): Promise<OrgTreeSettingCatalogOption[]> => {
    const response = await api.get(API_PATHS.orgTree.meta.contactTypes)
    return getCatalogListPayload<OrgTreeSettingCatalogOption>(response.data)
  },

  createSocialMedia: async (payload: OrgTreeNodeSettingPayload) => {
    const response = await api.post(API_PATHS.orgTree.settings.addSocial, payload)
    return response.data
  },

  createContact: async (payload: OrgTreeNodeSettingPayload) => {
    const response = await api.post(API_PATHS.orgTree.settings.addContact, payload)
    return response.data
  },

  updateNodeSetting: async (settingId: string | number, payload: OrgTreeNodeSettingUpdatePayload) => {
    const response = await api.post(API_PATHS.orgTree.settings.edit(settingId), payload)
    return response.data
  },

  deleteNodeSetting: async (settingId: string | number) => {
    const response = await api.delete(API_PATHS.orgTree.settings.delete(settingId))
    return response.data
  },

  listServiceAreas: async (nodeId: string | number, serviceInstanceId?: string | null): Promise<OrgTreeServiceArea[]> => {
    const response = await api.get(API_PATHS.orgTree.serviceAreas.list, {
      params: {
        node: nodeId,
        active: 1,
        ...(serviceInstanceId ? { service_instance_id: serviceInstanceId } : {}),
      },
    })

    return getListPayload<OrgTreeServiceArea>(response.data)
  },

  getServiceArea: async (serviceAreaId: string | number): Promise<OrgTreeServiceArea | null> => {
    const response = await api.get(API_PATHS.orgTree.serviceAreas.detail(serviceAreaId))
    return getObjectPayload<OrgTreeServiceArea>(response.data)
  },

  createServiceArea: async (payload: OrgTreeServiceAreaPayload) => {
    const response = await api.post(API_PATHS.orgTree.serviceAreas.create, payload)
    return response.data
  },

  updateServiceArea: async (serviceAreaId: string | number, payload: Partial<OrgTreeServiceAreaPayload>) => {
    const response = await api.patch(API_PATHS.orgTree.serviceAreas.update(serviceAreaId), payload)
    return response.data
  },

  deleteServiceArea: async (serviceAreaId: string | number) => {
    const response = await api.delete(API_PATHS.orgTree.serviceAreas.delete(serviceAreaId))
    return response.data
  },

  listBusinessDays: async (serviceInstanceId: string | number, type = 1): Promise<OrgTreeBusinessDay[]> => {
    const response = await api.get(API_PATHS.orgTree.businessDays.list, {
      params: {
        type,
        service_instance_id: serviceInstanceId,
      },
    })

    return getListPayload<OrgTreeBusinessDay>(response.data)
  },

  createBusinessDay: async (serviceInstanceId: string | number, payload: OrgTreeBusinessDayPayload) => {
    const response = await api.post(API_PATHS.orgTree.businessDays.create, payload, {
      params: { service_instance_id: serviceInstanceId },
    })
    return unwrapMutationPayload<OrgTreeBusinessDay>(response.data)
  },

  updateBusinessDay: async (serviceInstanceId: string | number, businessDayId: string | number, payload: Partial<OrgTreeBusinessDayPayload>) => {
    const response = await api.patch(API_PATHS.orgTree.businessDays.update(businessDayId), payload, {
      params: { service_instance_id: serviceInstanceId },
    })
    return unwrapMutationPayload<OrgTreeBusinessDay>(response.data)
  },

  deleteBusinessDay: async (serviceInstanceId: string | number, businessDayId: string | number) => {
    const response = await api.delete(API_PATHS.orgTree.businessDays.delete(businessDayId), {
      params: { service_instance_id: serviceInstanceId },
    })
    return response.data
  },

  createBusinessHour: async (serviceInstanceId: string | number, payload: OrgTreeBusinessHourPayload) => {
    const response = await api.post(API_PATHS.orgTree.businessHours.create, payload, {
      params: { service_instance_id: serviceInstanceId },
    })
    return unwrapMutationPayload<OrgTreeBusinessHour>(response.data)
  },

  deleteBusinessHour: async (serviceInstanceId: string | number, businessHourId: string | number) => {
    const response = await api.delete(API_PATHS.orgTree.businessHours.delete(businessHourId), {
      params: { service_instance_id: serviceInstanceId },
    })
    return response.data
  },

  listServiceVariants: async (): Promise<OrgTreeServiceVariant[]> => {
    const response = await api.get(API_PATHS.orgTree.serviceVariants.list)
    const rows = getListPayload<UnknownRecord>(response.data).filter(isBusinessServiceVariant)
    return rows.map((row) => ({
      id: (row.id as string | number) ?? '',
      name: getServiceVariantLabel(row),
      value: (row.value as string | null | undefined) ?? null,
    }))
  },

  listPublicPermittedServices: async (orgInstanceId: string): Promise<Array<{ id: string | number; label: string }>> => {
    const response = await api.get(API_PATHS.orgTree.instanceVariants.publicList, {
      params: { org_instance: orgInstanceId },
    })
    const rows = getListPayload<UnknownRecord>(response.data)
    return rows
      .filter(isBranchServiceVariant)
      .map((row) => ({
        id: (row.service_id as string | number) ?? (row.id as string | number) ?? '',
        label: String(row.value ?? row.id ?? 'Unknown'),
      }))
  },

  listOutboundServiceVariants: async (orgInstanceId?: string | null): Promise<OrgTreeServiceVariant[]> => {
    const response = orgInstanceId
      ? await api.get(API_PATHS.orgTree.instanceVariants.list, {
          params: {
            org_instance: orgInstanceId,
          },
        })
      : await api.get(API_PATHS.orgTree.serviceVariants.outboundList)

    const rows = getListPayload<UnknownRecord>(response.data)
      .filter(isBusinessServiceVariant)
      .filter(isBranchServiceVariant)

    const deduped = new Map<string, OrgTreeServiceVariant>()

    for (const row of rows) {
      const serviceId = (row.service_id as string | number | null | undefined) ?? null
      const serviceUniqueId = getServiceUniqueId(row)
      const variantId = orgInstanceId
        ? ((row.service_variant as string | number | undefined) ?? serviceUniqueId ?? (row.id as string | number) ?? '')
        : (serviceUniqueId ?? (row.id as string | number) ?? '')
      const key = String(serviceUniqueId || serviceId || variantId || getServiceVariantLabel(row)).trim()

      if (!key || deduped.has(key)) {
        continue
      }

      deduped.set(key, {
        id: variantId,
        name: getServiceVariantLabel(row),
        value: (row.value as string | null | undefined) ?? null,
        serviceId,
        serviceName: (row.service_name as string | null | undefined) ?? null,
        serviceUniqueId,
      })
    }

    return Array.from(deduped.values())
  },

  listFeatureScopeVariants: async (orgInstanceId?: string | null): Promise<OrgTreeServiceVariant[]> => {
    const response = orgInstanceId
      ? await api.get(API_PATHS.orgTree.instanceVariants.list, {
          params: {
            org_instance: orgInstanceId,
          },
        })
      : await api.get(API_PATHS.orgTree.serviceVariants.outboundList)

    const rows = getListPayload<UnknownRecord>(response.data)
      .filter(isBusinessServiceVariant)

    return rows.map((row) => ({
      id: orgInstanceId
        ? ((row.service_variant as string | number | undefined) ?? (row.id as string | number | undefined) ?? '')
        : ((row.unique_service_id as string | number | undefined) ?? (row.id as string | number) ?? ''),
      name: getServiceVariantLabel(row),
      value: (row.value as string | null | undefined) ?? null,
      serviceId: (row.service_id as string | number | null | undefined) ?? null,
      serviceName: (row.service_name as string | null | undefined) ?? null,
      serviceUniqueId: getServiceUniqueId(row),
    }))
  },

  listOutboundServices: async (orgInstanceId?: string | null): Promise<OrgTreeServiceVariant[]> => {
    const response = orgInstanceId
      ? await api.get(API_PATHS.orgTree.instanceVariants.list, {
          params: {
            org_instance: orgInstanceId,
          },
        })
      : await api.get(API_PATHS.orgTree.serviceVariants.outboundList)

    const rows = getListPayload<UnknownRecord>(response.data)
      .filter(isBusinessServiceVariant)

    const deduped = new Map<string, OrgTreeServiceVariant>()

    for (const row of rows) {
      const serviceId = (row.service_id as string | number | null | undefined) ?? null
      const serviceUniqueId = getServiceUniqueId(row)
      const serviceName = (row.service_name as string | null | undefined) ?? null
      const key = String(serviceId ?? serviceUniqueId ?? row.id ?? '')

      if (!key || deduped.has(key)) {
        continue
      }

      deduped.set(key, {
        id: serviceId ?? serviceUniqueId ?? ((row.id as string | number | undefined) ?? ''),
        name: serviceName || getServiceVariantLabel(row),
        value: serviceName || ((row.value as string | null | undefined) ?? null),
        serviceId,
        serviceName,
        serviceUniqueId,
      })
    }

    return Array.from(deduped.values())
  },

  listServiceInstancesWithNode: async (
    nodeId: string | number,
    options?: { includeInactive?: boolean; lightweight?: boolean },
  ): Promise<OrgTreeServiceInstance[]> => {
    const lightweight = options?.lightweight ?? true
    const response = await api.get(API_PATHS.orgTree.serviceInstances.listWithNode, {
      params: {
        node_id: nodeId,
        ...(options?.includeInactive ? { include_inactive: 1 } : {}),
        ...(lightweight ? { lightweight: 1 } : {}),
        page_size: 1000,
      },
    })

    return getListPayload<OrgTreeServiceInstance>(response.data)
      .map((row) => normalizeServiceInstanceApiRow(row))
      .filter((row): row is OrgTreeServiceInstance => row != null)
  },

  /**
   * ServiceInstances/List/ for level-variant directory mode. Tree scope comes from the workspace
   * anchor `service_instance_id` (backend resolves primary_node); do not send node_id.
   */
  listServiceInstancesByWorkspaceInstanceAndLevelVariant: async (
    serviceInstanceId: string | number,
    uniqueLevelVariantId: string,
    pageSize = 1000,
    options?: { lightweight?: boolean },
  ): Promise<OrgTreeServiceInstance[]> => {
    const lightweight = options?.lightweight ?? true
    const normalizedLevelVariant = String(uniqueLevelVariantId || '').trim()
    const genericLevelVariant = normalizedLevelVariant.replace(/-lvl-variant$/i, '') || normalizedLevelVariant

    const response = await api.get(API_PATHS.orgTree.serviceInstances.list, {
      params: {
        service_instance_id: serviceInstanceId,
        lvl_variant: genericLevelVariant,
        // Always send the exact selected level variant unique id from URL/sidebar
        // so backend filtering is deterministic for both system and custom variants.
        unique_level_variant_id: normalizedLevelVariant || undefined,
        include_child_level_variants: 1,
        ...(lightweight ? { lightweight: 1 } : {}),
        page_size: pageSize,
      },
    })

    return getListPayload<OrgTreeServiceInstance>(response.data)
      .map((row) => normalizeServiceInstanceApiRow(row))
      .filter((row): row is OrgTreeServiceInstance => row != null)
  },

  listWorkspaceChildServiceInstances: async (
    serviceInstanceId: string | number,
    options?: { includeInactive?: boolean; pageSize?: number },
  ): Promise<OrgTreeServiceInstance[]> => {
    const response = await api.get(API_PATHS.orgTree.serviceInstances.list, {
      params: {
        service_instance_id: serviceInstanceId,
        ...(options?.includeInactive ? { include_inactive: 1 } : {}),
        page_size: options?.pageSize ?? 1000,
      },
    })

    return getListPayload<OrgTreeServiceInstance>(response.data)
      .map((row) => normalizeServiceInstanceApiRow(row))
      .filter((row): row is OrgTreeServiceInstance => row != null)
  },

  getServiceInstance: async (serviceInstanceId: string | number): Promise<OrgTreeServiceInstance | null> => {
    const response = await api.get(API_PATHS.orgTree.serviceInstances.get(serviceInstanceId))
    return normalizeServiceInstanceApiRow(getObjectPayload<OrgTreeServiceInstance>(response.data))
  },

  getOptionalServiceInstance: async (serviceInstanceId: string | number): Promise<OrgTreeServiceInstance | null> => {
    try {
      const response = await api.get(
        API_PATHS.orgTree.serviceInstances.get(serviceInstanceId),
        getOptionalServiceInstanceRequestConfig(serviceInstanceId),
      )

      return normalizeServiceInstanceApiRow(getObjectPayload<OrgTreeServiceInstance>(response.data))
    } catch {
      return null
    }
  },

  findAncestorRoot: async (serviceInstanceId: string | number): Promise<OrgTreeServiceInstance | null> => {
    try {
      const response = await api.get(
        API_PATHS.orgTree.serviceInstances.ancestorRoot(serviceInstanceId),
        getOptionalServiceInstanceRequestConfig(serviceInstanceId),
      )

      return normalizeServiceInstanceApiRow(getObjectPayload<OrgTreeServiceInstance>(response.data))
    } catch {
      return null
    }
  },

  getServiceInstanceForEditForm: async (serviceInstanceId: string | number): Promise<OrgTreeServiceInstance | null> => {
    const targetId = String(serviceInstanceId ?? '').trim()
    if (!targetId) {
      return null
    }

    try {
      const response = await api.get(
        API_PATHS.orgTree.serviceInstances.get(targetId),
        getOptionalServiceInstanceRequestConfig(targetId, { lightweight: false }),
      )

      return normalizeServiceInstanceApiRow(getObjectPayload<OrgTreeServiceInstance>(response.data))
    } catch {
      return null
    }
  },

  getServiceInstanceByIdentifier: async (identifier: string): Promise<OrgTreeServiceInstance | null> => {
    try {
      const response = await api.get(
        API_PATHS.orgTree.serviceInstances.getByIdentifier(identifier),
        getOptionalServiceInstanceRequestConfig(),
      )

      return normalizeServiceInstanceApiRow(getObjectPayload<OrgTreeServiceInstance>(response.data))
    } catch {
      return null
    }
  },

  resolveTenantThemeByHost: async (host: string): Promise<OrgTreeServiceInstance | null> => {
    const normalizedHost = String(host || '').trim()
    if (!normalizedHost) {
      return null
    }

    try {
      const response = await api.get(
        API_PATHS.orgTree.serviceInstances.resolveTenantTheme,
        {
          ...getOptionalServiceInstanceRequestConfig(),
          params: { host: normalizedHost },
        },
      )

      return normalizeServiceInstanceApiRow(getObjectPayload<OrgTreeServiceInstance>(response.data))
    } catch {
      return null
    }
  },

  getPublicServiceInstance: async (
    orgInstanceId: string | number,
    options?: { lightweight?: boolean },
  ): Promise<OrgTreeServiceInstance | null> => {
    const normalizedReference = String(orgInstanceId ?? '').trim()
    if (/^node-\d+$/i.test(normalizedReference)) {
      return null
    }

    try {
      const response = await api.get(
        API_PATHS.orgTree.serviceInstances.getPublic(orgInstanceId),
        getOptionalServiceInstanceRequestConfig(undefined, {
          lightweight: options?.lightweight ?? true,
        }),
      )

      return normalizeServiceInstanceApiRow(getObjectPayload<OrgTreeServiceInstance>(response.data))
    } catch {
      return null
    }
  },

  resolvePublicSignupServiceInstance: async (reference: string): Promise<OrgTreeServiceInstance | null> => {
    const normalizedReference = reference.trim()
    if (!normalizedReference) {
      return null
    }

    const nodeMatch = normalizedReference.match(/^node-(\d+)$/i)
    if (nodeMatch) {
      const nodeId = nodeMatch[1]
      const scopedRows = await orgTreeApi.listServiceInstancesWithNode(nodeId, { lightweight: true })
      const orgScoped = scopedRows.find((row) => isOrganizationServiceVariant(
        String(row.level_variant_unique_id ?? row.service_variant ?? '').trim() || null,
        String(row.service_variant_name ?? row.service_variant_label ?? '').trim().toLowerCase() || null,
      ))
      return normalizeServiceInstanceApiRow(orgScoped ?? scopedRows[0] ?? null)
    }

    const publicMatch = await orgTreeApi.getPublicServiceInstance(normalizedReference)
    if (publicMatch) {
      return publicMatch
    }

    const directMatch = await orgTreeApi.getOptionalServiceInstance(normalizedReference)
    if (directMatch) {
      return directMatch
    }

    return orgTreeApi.getServiceInstanceByIdentifier(normalizedReference)
  },

  createServiceInstance: async (payload: ServiceInstancePayload) => {
    const response = await api.post(API_PATHS.orgTree.serviceInstances.add, payload)
    return unwrapMutationPayload<UnknownRecord>(response.data)
  },

  createClientStore: async (payload: ServiceInstancePayload) => {
    const response = await api.post(API_PATHS.orgTree.serviceInstances.createClient, payload)
    return unwrapMutationPayload<UnknownRecord>(response.data)
  },

  updateServiceInstance: async (
    serviceInstanceId: string | number,
    payload: ServiceInstancePayload | FormData,
    config?: AppRequestConfig,
  ) => {
    const response = await api.patch(API_PATHS.orgTree.serviceInstances.update(serviceInstanceId), payload, config)
    return response.data
  },

  updateServiceInstanceAdmin: async (
    serviceInstanceId: string | number,
    payload: {
      user_id: string | number
      unique_level_variant_id?: string | null
      include_child_level_variants?: boolean | number | string
    },
  ) => {
    const response = await api.post(API_PATHS.orgTree.serviceInstances.adminUpdate(serviceInstanceId), payload)
    return response.data
  },

  listServiceInstanceMedia: async (serviceInstanceId: string | number): Promise<OrgTreeServiceInstanceMediaState | null> => {
    const response = await api.get(API_PATHS.orgTree.serviceInstances.mediaList(serviceInstanceId))
    return getObjectPayload<OrgTreeServiceInstanceMediaState>(response.data)
  },

  uploadServiceInstanceMedia: async (serviceInstanceId: string | number, mediaKey: string, file: File) => {
    const formData = new FormData()
    formData.append('media_key', mediaKey)
    formData.append('file', file)
    const response = await api.post(API_PATHS.orgTree.serviceInstances.mediaUpload(serviceInstanceId), formData)
    return response.data
  },

  deleteServiceInstanceMedia: async (serviceInstanceId: string | number, mediaId: string | number) => {
    const response = await api.delete(API_PATHS.orgTree.serviceInstances.mediaDelete(serviceInstanceId, mediaId))
    return response.data
  },

  getServiceInstanceCredentials: async (serviceInstanceId: string | number): Promise<OrgTreeCredentialSettings | null> => {
    const response = await api.get(API_PATHS.orgTree.serviceInstances.credentialsGet(serviceInstanceId))
    return getObjectPayload<OrgTreeCredentialSettings>(response.data)
  },

  getServiceInstanceMapsClientConfig: async (serviceInstanceId: string | number): Promise<OrgTreeMapsClientConfig | null> => {
    const response = await api.get(API_PATHS.orgTree.serviceInstances.mapsClientConfigGet(serviceInstanceId))
    return getObjectPayload<OrgTreeMapsClientConfig>(response.data)
  },

  updateServiceInstanceCredentials: async (serviceInstanceId: string | number, payload: ServiceInstancePayload | Record<string, unknown> | FormData) => {
    const response = await api.patch(API_PATHS.orgTree.serviceInstances.credentialsUpdate(serviceInstanceId), payload)
    return response.data
  },

  getServiceInstanceAppSettings: async (serviceInstanceId: string | number): Promise<OrgTreeAppSettings | null> => {
    const response = await api.get(API_PATHS.orgTree.serviceInstances.appSettingsGet(serviceInstanceId))
    return getObjectPayload<OrgTreeAppSettings>(response.data)
  },

  updateServiceInstanceAppSettings: async (serviceInstanceId: string | number, payload: OrgTreeAppSettings): Promise<OrgTreeAppSettings | null> => {
    const response = await api.patch(API_PATHS.orgTree.serviceInstances.appSettingsUpdate(serviceInstanceId), payload)
    return getObjectPayload<OrgTreeAppSettings>(response.data)
  },

  testServiceInstanceCredentials: async (serviceInstanceId: string | number, payload: OrgTreeCredentialTestPayload) => {
    const response = await api.post(API_PATHS.orgTree.serviceInstances.credentialsTest(serviceInstanceId), payload)
    return response.data
  },

  previewServiceInstanceTemplate: async (
    serviceInstanceId: string | number,
    payload: OrgTreeSignupTemplatePreviewPayload,
  ): Promise<OrgTreeSignupTemplatePreview | null> => {
    const response = await api.post(API_PATHS.orgTree.serviceInstances.templatePreview(serviceInstanceId), payload)
    return getObjectPayload<OrgTreeSignupTemplatePreview>(response.data)
  },

  searchServiceInstanceLocation: async (
    serviceInstanceId: string | number,
    query: string,
    limit = 5,
  ): Promise<OrgTreeLocationSearchResponse> => {
    const response = await api.get(API_PATHS.orgTree.serviceInstances.locationSearch(serviceInstanceId), {
      params: {
        q: query,
        limit,
      },
    })

    const payload = getObjectPayload<OrgTreeLocationSearchResponse>(response.data)
    return {
      provider: payload?.provider ?? null,
      configured_provider: payload?.configured_provider ?? null,
      provider_source: payload?.provider_source ?? null,
      fallback_used: payload?.fallback_used ?? false,
      results: Array.isArray(payload?.results)
        ? payload!.results.map((entry) => ({
            ...entry,
            city: getMapSearchCity(entry),
          }))
        : [],
    }
  },

  deleteServiceInstance: async (serviceInstanceId: string | number) => {
    const response = await api.delete(API_PATHS.orgTree.serviceInstances.delete(serviceInstanceId))
    return response.data
  },

  listCountries: async (): Promise<OrgTreeCountry[]> => {
    const response = await api.get(API_PATHS.orgTree.meta.countries)
    return getListPayload<OrgTreeCountry>(response.data)
  },

  listStates: async (countryId: string | number): Promise<OrgTreeState[]> => {
    const response = await api.get(API_PATHS.orgTree.meta.states(countryId))
    return getListPayload<OrgTreeState>(response.data)
  },

  listCurrencies: async (): Promise<OrgTreeCurrency[]> => {
    const response = await api.get(API_PATHS.orgTree.meta.currencies)
    return getListPayload<OrgTreeCurrency>(response.data)
  },

  listTimezones: async (): Promise<OrgTreeTimezone[]> => {
    const response = await api.get(API_PATHS.orgTree.meta.timezones)
    return getListPayload<OrgTreeTimezone>(response.data)
  },

  listInAppInstances: async (serviceInstanceId: string | number): Promise<OrgTreeServiceInstance[]> => {
    const scopeId = String(serviceInstanceId)
    const response = await api.get(API_PATHS.orgTree.serviceInstances.list, {
      params: {
        service_instance_id: scopeId,
        in_app_only: true,
        include_inactive: false,
        page_size: 1000,
      },
    })

    const rows = getListPayload<OrgTreeServiceInstance>(response.data)
      .map((row) => normalizeServiceInstanceApiRow(row))
      .filter((row): row is OrgTreeServiceInstance => Boolean(row))

    const inApps = rows.filter((row) => {
      const variantUnique = String(
        row.service_data?.variant_unique
        ?? row.service_variant
        ?? row.service_id
        ?? '',
      ).trim()
      const variantLabel = String(
        row.service_variant_name
        ?? row.service_variant_label
        ?? row.name
        ?? '',
      ).trim()

      if (isOrganizationServiceVariant(variantUnique, variantLabel)) {
        return false
      }

      // Keep attached in-apps (incl. Services with lvl_variant=in-apps); drop BRANCH Services.
      return isInAppChildServiceVariant(variantUnique, variantLabel, {
        levelVariantUniqueId: row.level_variant_unique_id,
        levelVariantName: row.level_variant_name,
        hierarchyTierCode: row.level_variant_vertical_system_code,
      })
    })

    return inApps
  },
}